import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const CollectionsPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Collections', icon: '🌟' },
    { id: 'upholstery', name: 'Upholstery', icon: '🪑' },
    { id: 'apparel', name: 'Apparel', icon: '👕' },
    { id: 'technical', name: 'Technical', icon: '🔬' },
    { id: 'heritage', name: 'Heritage', icon: '🏛️' },
    { id: 'luxury', name: 'Luxury', icon: '💎' }
  ];

  const collections = [
    {
      id: 'museo',
      name: 'Museo Textiles',
      category: 'heritage',
      description: 'Timeless patterns inspired by classical art and architecture',
      image: '/images/Buy Clothing Fabrics by Category, Online Shop - SK Textiles/imgi_20_CHIFFON-WOVEN_WHITE_YS135Z150-03000-450x450.jpg',
      gradient: 'from-royal-purple to-amethyst'
    },
    {
      id: 'chintz',
      name: 'Chintz Collection',
      category: 'luxury',
      description: 'Exquisite floral patterns with rich, vibrant colors',
      image: '/images/Print Fabrics – Lively Fabrics/imgi_28_9168bb4e-1d43-4a2c-8fb8-cc7c516f0caa.jpg',
      gradient: 'from-warm-gold to-sunset-orange'
    },
    {
      id: 'damask',
      name: 'Damask Patterns',
      category: 'luxury',
      description: 'Sophisticated geometric designs for premium applications',
      image: '/images/Caversham Textiles and Designs – Modern South African Designs/imgi_136_HC-Whole-Fig-Tea-Towel-02-SQ.jpg',
      gradient: 'from-coral to-rose-500'
    },
    {
      id: 'chenille',
      name: 'Chenille Fabric',
      category: 'upholstery',
      description: 'Luxuriously soft and durable for furniture and decor',
      image: '/images/Plain-Mini-Matt-Fabrics/imgi_221_MiniMatt_Colour07_Twist-Edit.jpg',
      gradient: 'from-ocean-blue to-teal-wave'
    },
    {
      id: 'fire-retardant',
      name: 'Fire-retardant',
      category: 'technical',
      description: 'Safety-focused fabrics for commercial and public spaces',
      image: '/images/The Stain Guard Range™️_ Protection🍷 – Ragland Fabrics/imgi_259_MarsalaTS.png',
      gradient: 'from-deep-purple to-royal-purple'
    },
    {
      id: 'polo',
      name: 'Polo T-shirt',
      category: 'apparel',
      description: 'Premium cotton blends for comfortable, stylish clothing',
      image: '/images/Online Clothing Fabrics Shop - Vega Textiles/imgi_149_IMG_1320.jpg',
      gradient: 'from-sky-blue to-ocean-blue'
    }
  ];

  const filteredCollections = selectedCategory === 'all' 
    ? collections 
    : collections.filter(collection => collection.category === selectedCategory);

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background with gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-midnight via-deep-purple to-royal-purple">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1582735689369-4fe89db7114c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')] bg-cover bg-center bg-no-repeat opacity-20"></div>
        </div>

        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* Breadcrumb */}
            <div className="flex items-center justify-center space-x-2 mb-8">
              <span className="text-lavender/60">Home</span>
              <span className="text-lavender/40">/</span>
              <span className="text-lavender">Collections</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-serif font-bold text-white mb-8 animate-fade-in drop-shadow-2xl">
              Our Collections
            </h1>
            
            {/* Subtitle */}
            <p className="text-xl md:text-2xl lg:text-3xl text-lavender mb-10 font-medium animate-slide-up drop-shadow-xl max-w-3xl mx-auto">
              Discover our curated selection of premium textiles
            </p>
            
            {/* Description */}
            <p className="text-lg md:text-xl text-white/90 mb-12 max-w-4xl mx-auto leading-relaxed animate-slide-up drop-shadow-lg">
              From timeless heritage patterns to cutting-edge technical fabrics, 
              each collection represents the pinnacle of textile craftsmanship. 
              Explore our diverse range designed for every application and aesthetic preference.
            </p>

            {/* Collection Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-serif font-bold text-gradient mb-2">{collections.length}</div>
                <div className="text-lavender/80">Collections</div>
              </div>
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-serif font-bold text-gradient mb-2">6</div>
                <div className="text-lavender/80">Categories</div>
              </div>
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-serif font-bold text-gradient mb-2">1000+</div>
                <div className="text-lavender/80">Fabric Variants</div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute bottom-8 left-8 hidden lg:block">
          <div className="w-32 h-32 border-2 border-lavender/30 rotate-45 rounded-3xl animate-float"></div>
        </div>
        <div className="absolute top-20 right-12 hidden lg:block">
          <div className="w-20 h-20 bg-gradient-to-br from-warm-gold/20 to-sunset-orange/20 rounded-full border border-warm-gold/30 animate-pulse-slow"></div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-16 bg-gradient-primary">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-gradient mb-4">
              Browse by Category
            </h2>
            <p className="text-lg text-sage max-w-2xl mx-auto">
              Find exactly what you're looking for with our organized collection categories
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center space-x-3 px-6 py-4 rounded-2xl font-medium transition-all duration-300 ${
                  selectedCategory === category.id
                    ? 'bg-gradient-to-r from-royal-purple to-amethyst text-white shadow-lg scale-105'
                    : 'bg-white/80 text-sage hover:bg-white hover:text-royal-purple hover:scale-105 border border-lavender/30'
                }`}
              >
                <span className="text-xl">{category.icon}</span>
                <span>{category.name}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Collections Grid */}
      <section className="py-20 bg-gradient-secondary">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-gradient-gold mb-4">
              {selectedCategory === 'all' ? 'All Collections' : `${categories.find(c => c.id === selectedCategory)?.name}`}
            </h2>
            <p className="text-lg text-sage max-w-2xl mx-auto">
              {filteredCollections.length} collection{filteredCollections.length !== 1 ? 's' : ''} found
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCollections.map((collection) => (
              <div
                key={collection.id}
                className="group bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105 border border-lavender/30"
              >
                <div className="relative overflow-hidden h-64">
                  <img
                    src={collection.image}
                    alt={collection.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute top-4 left-4">
                    <span className={`bg-gradient-to-r ${collection.gradient} text-white px-4 py-2 text-sm font-medium rounded-full shadow-lg`}>
                      {collection.category}
                    </span>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-midnight/60 via-deep-purple/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300"></div>
                </div>
                
                <div className="p-8">
                  <h3 className="text-2xl font-serif font-semibold text-charcoal mb-4">
                    {collection.name}
                  </h3>
                  <p className="text-sage mb-6 leading-relaxed">
                    {collection.description}
                  </p>
                  <Link 
                    to={`/collections/${collection.id}`} 
                    className="w-full bg-gradient-to-r from-royal-purple to-amethyst text-white py-3 rounded-2xl font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 border border-royal-purple/30 inline-block text-center"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {filteredCollections.length === 0 && (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-2xl font-serif font-semibold text-charcoal mb-2">No Collections Found</h3>
              <p className="text-sage">Try selecting a different category or check back later for new additions.</p>
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-accent">
        <div className="container-custom text-center">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-gradient mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-sage mb-8 max-w-2xl mx-auto">
            Contact our team to discuss your specific textile needs and discover how our collections can bring your vision to life.
          </p>
          <div className="flex justify-center">
            <Link 
              to="/contact" 
              className="btn-primary"
            >
              Request Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CollectionsPage;
